import clsx from 'clsx';
import { ChevronDown, ChevronUp, Download, ListChecks, ShieldAlert, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EFFICIENCY_FAST, EFFICIENCY_GOOD, EFFICIENCY_LOW, PLAN_COVERAGE_LOW, efficiencyTone, isoWeek } from '@pz/core';
import { AreaBars } from '../components/areas';
import { HourTerms, HourTypeBars, coverageLow, useCoverageText, useHourTypesTitle } from '../components/hour-types';
import { Avatar } from '../components/avatar';
import { HBars, WeekBars } from '../components/charts';
import { Button, Checkbox, EmptyState, ErrorBox, Loading, Modal, Notice, PageHeader, Panel, Pill, Select, Stat, td, th, useToast } from '../components/ui';
import { saveFile } from '../lib/download';
import { d, h, hh, pct } from '../lib/format';
import { useQ } from '../lib/hooks';
import { ScrollTable } from '../components/virtual';
import { useMe } from '../lib/me';
import { rpc } from '../lib/rpc';
import { RangeButtons, useRange } from './Cockpit';
import { EfficiencyTable } from './Overview';

type SortKey = 'name' | 'balance' | 'billable' | 'flags';

/**
 * The figures on this page, one entry per criterion with the table columns it fills (1-based, 1 = person). Nothing is
 * measured that is not listed here; the panel states what each criterion means so the evaluation can be explained.
 */
const CRITERIA = [
  { key: 'balance', cols: [2, 3, 4] },
  { key: 'weeks', cols: [5] },
  { key: 'billable', cols: [6] },
  { key: 'projectShare', cols: [7] },
  { key: 'efficiency', cols: [8] },
  { key: 'punctuality', cols: [9] },
  { key: 'corrections', cols: [10] },
] as const;

const HIDDEN_KEY = 'pz-eval-hidden';

function readHidden(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Mitarbeiterauswertung (staff review): the place where management looks at the personal figures of
 * each employee. Confidential; the page view and every opened person are written to the audit log.
 * "Auslastung" is a different tool: capacity planning (who has time), not an assessment.
 */
export function EvaluationPage() {
  const { t } = useTranslation();
  const { can } = useMe();
  const toast = useToast();
  const coverageText = useCoverageText();
  const hourTypesTitle = useHourTypesTitle();
  const r = useRange('last3m');
  const q = useQ('kpi.team', { from: r.from, to: r.to }, { enabled: can('kpi.team.view') });
  const people = useQ('people.list', {}, { enabled: can('kpi.team.view') });
  const avatars = new Map((people.data?.people ?? []).map((p) => [p.id, p.avatarAt]));
  const [person, setPerson] = useState<{ id: string; name: string } | null>(null);
  const [sort, setSort] = useState<SortKey>('name');
  const [exporting, setExporting] = useState(false);
  // which criteria this view shows: a choice of the viewer for this device, the figures themselves do not change
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [hidden, setHidden] = useState<string[]>(readHidden);
  const toggleCriterion = (key: string) =>
    setHidden((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      try {
        localStorage.setItem(HIDDEN_KEY, JSON.stringify(next));
      } catch {
        /* storage not available: the choice only lasts for this visit */
      }
      return next;
    });
  const showAll = () => {
    setHidden([]);
    try {
      localStorage.removeItem(HIDDEN_KEY);
    } catch {
      /* storage not available */
    }
  };
  const hiddenCols = CRITERIA.filter((c) => hidden.includes(c.key)).flatMap((c) => c.cols);

  const rows = useMemo(() => {
    const list = [...(q.data?.rows ?? [])];
    const num = (x: number) => (Number.isFinite(x) ? x : -Infinity);
    switch (sort) {
      case 'balance':
        return list.sort((a, b) => a.balanceMinutes - b.balanceMinutes);
      case 'billable':
        return list.sort((a, b) => num(b.billableShare) - num(a.billableShare));
      case 'flags':
        return list.sort((a, b) => b.underWeeks + b.overWeeks - (a.underWeeks + a.overWeeks));
      default:
        return list.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [q.data, sort]);

  if (!can('kpi.team.view')) return <EmptyState>{t('evaluation.noAccess')}</EmptyState>;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('evaluation.title')} subtitle={t('evaluation.subtitle')} actions={<RangeButtons r={r} />} />
      <Notice tone="warn">
        <ShieldAlert className="mr-1 inline size-4" />
        {t('kpi.confidentialNote')}
      </Notice>
      <div className="grid gap-3 md:grid-cols-3">
        <Signal tone="good" icon={<Sparkles className="size-4" />} title={t('evaluation.signalFastTitle')} text={t('evaluation.signalFastText', { percent: Math.round(EFFICIENCY_FAST * 100) })} />
        <Signal tone="warn" icon={<ChevronUp className="size-4" />} title={t('evaluation.signalOverTitle')} text={t('evaluation.signalOverText', { hours: h(q.data?.toleranceMinutes ?? 120, 0) })} />
        <Signal tone="bad" icon={<ChevronDown className="size-4" />} title={t('evaluation.signalUnderTitle')} text={t('evaluation.signalUnderText', { hours: h(q.data?.toleranceMinutes ?? 120, 0) })} />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {q.data && <span className="num text-xs text-graphite">{t('kpi.asOf', { from: d(q.data.since), to: d(q.data.asOf) })}</span>}
        <Select className="ml-auto w-56" value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label={t('evaluation.sort')}>
          {(['name', 'balance', 'billable', 'flags'] as const).map((k) => (
            <option key={k} value={k}>
              {t(`evaluation.sortBy.${k}`)}
            </option>
          ))}
        </Select>
        <Button icon={<ListChecks className="size-4" />} onClick={() => setCriteriaOpen((v) => !v)} aria-pressed={criteriaOpen}>
          {t('evaluation.criteria.show')} ({CRITERIA.length - hidden.filter((k) => CRITERIA.some((c) => c.key === k)).length}/{CRITERIA.length})
        </Button>
        <Button
          icon={<Download className="size-4" />}
          loading={exporting}
          onClick={async () => {
            setExporting(true);
            try {
              saveFile(await rpc('kpi.teamExport', { from: r.from, to: r.to }));
            } catch (e) {
              toast.error(e);
            } finally {
              setExporting(false);
            }
          }}
        >
          {t('common.exportExcel')}
        </Button>
      </div>
      {criteriaOpen && <CriteriaPanel hidden={hidden} onToggle={toggleCriterion} onAll={showAll} toleranceHours={h(q.data?.toleranceMinutes ?? 120, 0)} />}
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && (
        <div className="grid grid-cols-2 gap-4 rounded-lg border border-line bg-sheet p-4 md:grid-cols-5">
          <Stat label={t('kpi.capacity')} value={hh(q.data.totals.capacityMinutes, 0)} />
          <Stat label={t('kpi.booked')} value={hh(q.data.totals.bookedMinutes, 0)} hint={pct(q.data.totals.capacityMinutes ? q.data.totals.bookedMinutes / q.data.totals.capacityMinutes : Number.NaN)} />
          <Stat label={t('kpi.billableShare')} value={pct(q.data.totals.bookedMinutes ? q.data.totals.billableMinutes / q.data.totals.bookedMinutes : Number.NaN)} />
          <Stat label={t('kpi.efficiency')} value={pct(q.data.totals.efficiencyRatio)} hint={t('cockpit.teamEfficiencyHint')} />
          <Stat label={t('cockpit.weekFlags')} value={`${q.data.totals.underWeeks} / ${q.data.totals.overWeeks}`} hint={t('cockpit.weekFlagsHint', { hours: h(q.data.toleranceMinutes, 0) })} />
        </div>
      )}
      {q.data && q.data.totals.unplannedMinutes > 0 && (
        // finished tasks without plan hours are not in the ratio: say how much of the work it covers (request 19)
        <Notice tone={q.data.totals.efficiencyCoverage < PLAN_COVERAGE_LOW ? 'warn' : 'neutral'}>
          {q.data.totals.efficiencyCoverage < PLAN_COVERAGE_LOW
            ? t('hourTypes.coverageNotice', { pct: pct(q.data.totals.efficiencyCoverage), hours: h(q.data.totals.unplannedMinutes, 1) })
            : t('hourTypes.coverageInfo', { hours: h(q.data.totals.unplannedMinutes, 1) })}
        </Notice>
      )}
      {rows.length > 0 && (
        <ScrollTable label={t('nav.evaluation')}>
          {hiddenCols.length > 0 && <style>{hiddenCols.map((c) => `.eval-table th:nth-child(${c}),.eval-table td:nth-child(${c}){display:none}`).join('')}</style>}
          <table className="eval-table w-full min-w-[1280px] text-sm">
            <thead className="border-b border-line bg-sheet-2">
              <tr>
                <th className={th}>{t('common.person')}</th>
                <th className={clsx(th, 'text-right')}>{t('kpi.capacity')}</th>
                <th className={clsx(th, 'text-right')}>{t('kpi.booked')}</th>
                <th className={clsx(th, 'text-right')}>{t('kpi.balance')}</th>
                <th className={th}>{t('cockpit.weekFlagsCol')}</th>
                <th className={clsx(th, 'text-right')}>{t('kpi.billableShare')}</th>
                <th className={clsx(th, 'text-right')} title={t('areas.shareHint')}>
                  {t('kpi.projectShare')}
                </th>
                <th className={clsx(th, 'text-right')}>{t('kpi.efficiency')}</th>
                <th className={clsx(th, 'text-right')}>{t('kpi.punctuality')}</th>
                <th className={clsx(th, 'text-right')}>{t('kpi.rejectionRate')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.personId} className="cursor-pointer border-b border-line/70 hover:bg-sheet-2" onClick={() => setPerson({ id: x.personId, name: x.name })}>
                  <td className={td}>
                    <div className="flex items-center gap-2">
                      <Avatar personId={x.personId} name={x.name} avatarAt={avatars.get(x.personId) ?? null} />
                      <div>
                        <div className="font-medium">{x.name}</div>
                        <div className="text-xs text-graphite">{x.discipline ?? ''}</div>
                      </div>
                    </div>
                  </td>
                  <td className={clsx(td, 'num text-right')}>{h(x.capacityMinutes, 1)}</td>
                  <td className={clsx(td, 'num text-right')}>{h(x.bookedMinutes, 1)}</td>
                  <td className={clsx(td, 'num text-right', x.balanceMinutes < 0 ? 'text-bad' : x.balanceMinutes > 0 ? 'text-warn' : '')}>
                    {x.balanceMinutes > 0 ? '+' : ''}
                    {h(x.balanceMinutes, 1)}
                  </td>
                  <td className={td}>
                    <span className="flex flex-wrap gap-1">
                      {x.underWeeks > 0 && (
                        <Pill tone="bad" title={t('kpi.underLegend')}>
                          <ChevronDown className="size-3" />
                          {x.underWeeks}
                        </Pill>
                      )}
                      {x.overWeeks > 0 && (
                        <Pill tone="warn" title={t('kpi.overLegend')}>
                          <ChevronUp className="size-3" />
                          {x.overWeeks}
                        </Pill>
                      )}
                      {!x.underWeeks && !x.overWeeks && <span className="text-xs text-graphite">{t('cockpit.noFlags', { count: x.finishedWeeks })}</span>}
                    </span>
                  </td>
                  <td className={clsx(td, 'num text-right')} title={hourTypesTitle(x.hourTypes)}>
                    {pct(x.billableShare)}
                    {x.hourTypes.nonBillableProject > 0 && (
                      <span className="block text-xs text-graphite">{t('hourTypes.nonBillableProject')}: {h(x.hourTypes.nonBillableProject, 1)} h</span>
                    )}
                  </td>
                  <td className={clsx(td, 'num text-right')}>
                    {pct(x.projectShare)}
                    {x.internalMinutes > 0 && <span className="block text-xs text-graphite">{t('areas.internalHours', { hours: h(x.internalMinutes, 1) })}</span>}
                  </td>
                  <td className={clsx(td, 'num text-right')} title={coverageText(x.efficiency) || undefined}>
                    {x.efficiency.tasks ? (
                      <span title={[t('kpi.efficiencyHint', { count: x.efficiency.tasks, plan: h(x.efficiency.earnedMinutes, 1), actual: h(x.efficiency.actualMinutes, 1) }), coverageText(x.efficiency)].filter(Boolean).join('\n')}>
                        <span className={clsx('font-semibold', efficiencyTone(x.efficiency.ratio) === 'good' ? 'text-good' : efficiencyTone(x.efficiency.ratio) === 'warn' ? 'text-warn' : '')}>{pct(x.efficiency.ratio)}</span>
                        <span className="block text-xs text-graphite">
                          {t('cockpit.tasksCount', { count: x.efficiency.tasks })}
                          {x.efficiency.fastTasks > 0 && (
                            <span className="ml-1 inline-flex items-center gap-0.5 text-good">
                              <Sparkles className="size-3" />
                              {x.efficiency.fastTasks}
                            </span>
                          )}
                        </span>
                      </span>
                    ) : (
                      '-'
                    )}
                    {x.efficiency.unplannedMinutes > 0 && (
                      <span className={clsx('block text-xs', coverageLow(x.efficiency) ? 'text-warn' : 'text-graphite')}>{t('hourTypes.coverageShort', { hours: h(x.efficiency.unplannedMinutes, 1) })}</span>
                    )}
                  </td>
                  <td className={clsx(td, 'num text-right')}>{Number.isFinite(x.punctuality) ? `${x.weeksOnTime}/${x.weeksDue}` : '-'}</td>
                  <td className={clsx(td, 'num text-right')}>{pct(x.rejectionRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollTable>
      )}
      <p className="text-xs text-graphite">{t('evaluation.hint')}</p>
      <HourTerms />
      <PersonDetail person={person} from={r.from} to={r.to} onClose={() => setPerson(null)} />
    </div>
  );
}

/** What each figure on the page means: the basis for explaining the evaluation to the works council and to the people evaluated. */
function CriteriaPanel({ hidden, onToggle, onAll, toleranceHours }: { hidden: string[]; onToggle: (key: string) => void; onAll: () => void; toleranceHours: string }) {
  const { t } = useTranslation();
  const { me } = useMe();
  const vars = { hours: toleranceHours, good: Math.round(EFFICIENCY_GOOD * 100), low: Math.round(EFFICIENCY_LOW * 100), days: me.settings.submitDeadlineDays ?? 1 };
  return (
    <Panel
      title={t('evaluation.criteria.title')}
      actions={
        <Button size="sm" variant="ghost" onClick={onAll}>
          {t('evaluation.criteria.all')}
        </Button>
      }
    >
      <p className="mb-3 text-sm text-graphite">{t('evaluation.criteria.intro')}</p>
      <ul className="grid gap-3 md:grid-cols-2">
        {CRITERIA.map((c) => (
          <li key={c.key}>
            <Checkbox
              label={<span className="font-medium">{t(`evaluation.criteria.${c.key}.name`)}</span>}
              hint={t(`evaluation.criteria.${c.key}.desc`, vars)}
              checked={!hidden.includes(c.key)}
              onChange={() => onToggle(c.key)}
            />
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-graphite">{t('evaluation.criteria.thresholds', vars)}</p>
    </Panel>
  );
}

function Signal({ tone, icon, title, text }: { tone: 'good' | 'warn' | 'bad'; icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className={clsx('flex gap-2 rounded-lg border px-3 py-2 text-sm', tone === 'good' ? 'border-good/30 bg-good-soft' : tone === 'warn' ? 'border-warn/30 bg-warn-soft' : 'border-bad/30 bg-bad-soft')}>
      <span className={clsx('mt-0.5', tone === 'good' ? 'text-good' : tone === 'warn' ? 'text-warn' : 'text-bad')}>{icon}</span>
      <span>
        <span className="block font-semibold">{title}</span>
        <span className="text-xs text-graphite">{text}</span>
      </span>
    </div>
  );
}

function PersonDetail({ person, from, to, onClose }: { person: { id: string; name: string } | null; from: string; to: string; onClose: () => void }) {
  const { t } = useTranslation();
  const coverageText = useCoverageText();
  const q = useQ('kpi.person', { personId: person?.id ?? '', from, to }, { enabled: !!person });
  return (
    <Modal open={!!person} onClose={onClose} wide title={person ? t('cockpit.personTitle', { name: person.name }) : ''}>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && (
        <div className="flex flex-col gap-5">
          <Notice tone="warn">
            <ShieldAlert className="mr-1 inline size-4" />
            {t('kpi.confidentialNote')}
          </Notice>
          {q.data.efficiency.unplannedMinutes > 0 && <Notice tone={coverageLow(q.data.efficiency) ? 'warn' : 'neutral'}>{coverageText(q.data.efficiency)}</Notice>}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <Stat
              label={t('kpi.efficiency')}
              value={q.data.efficiency.tasks ? pct(q.data.efficiency.ratio) : '-'}
              hint={q.data.efficiency.tasks ? t('kpi.efficiencyHint', { count: q.data.efficiency.tasks, plan: h(q.data.efficiency.earnedMinutes, 1), actual: h(q.data.efficiency.actualMinutes, 1) }) : t('kpi.efficiencyNone')}
            />
            <Stat
              label={t('kpi.solo')}
              value={q.data.efficiency.soloTasks ? pct(q.data.efficiency.soloRatio) : '-'}
              hint={t('kpi.soloHint', { count: q.data.efficiency.soloTasks })}
            />
            <Stat label={t('cockpit.saved')} value={q.data.efficiency.tasks ? `${q.data.efficiency.savedMinutes > 0 ? '+' : ''}${hh(q.data.efficiency.savedMinutes, 1)}` : '-'} hint={t('cockpit.savedHint')} />
            <Stat label={t('kpi.booked')} value={hh(q.data.weeks.reduce((s, w) => s + w.bookedMinutes, 0), 1)} />
            <Stat label={t('kpi.capacity')} value={hh(q.data.weeks.reduce((s, w) => s + (w.capacityMinutes ?? 0), 0), 1)} />
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('kpi.weekly')}</h3>
            <WeekBars
              data={q.data.weeks.map((w) => {
                const n = isoWeek(w.weekStart).week;
                return {
                  label: `${t('kpi.weekPrefix')} ${n}`,
                  axis: `${n}`,
                  booked: w.bookedMinutes,
                  billable: w.billableMinutes,
                  capacity: w.capacityMinutes,
                  comp: w.compensationMinutes,
                  state: w.state,
                  note: w.compensationMinutes ? t('week.compHint', { hours: h(w.compensationMinutes) }) : undefined,
                };
              })}
            />
          </div>
          <div>
            <h3 className="mb-1 text-sm font-semibold">{t('overview.planVsActual')}</h3>
            <p className="mb-2 text-xs text-graphite">{t('kpi.efficiencyExplain')}</p>
            {q.data.efficiency.items.length ? <EfficiencyTable items={q.data.efficiency.items} /> : <p className="text-sm text-graphite">{t('kpi.efficiencyNone')}</p>}
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('kpi.byProject')}</h3>
            <HBars head={t('common.project')} rows={q.data.byProject.map((p) => ({ label: `${p.number} ${p.name}`, value: p.minutes, billable: p.billable }))} />
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('hourTypes.title')}</h3>
            <HourTypeBars types={q.data.hourTypes} />
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">{t('areas.title')}</h3>
            <AreaBars areas={q.data.areas} />
          </div>
          <HourTerms />
        </div>
      )}
    </Modal>
  );
}
